import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { formatarMoeda } from '../../utils/formatters';

const getStatusColor = (status) => {
  const statusColors = {
    PENDENTE: 'warning',
    PAGO: 'success',
    ATRASADO: 'error',
    PROCESSANDO: 'info'
  };
  return statusColors[status] || 'default';
};

const getStatusLabel = (status) => {
  const statusLabels = {
    PENDENTE: 'Pendente',
    PAGO: 'Pago', 
    ATRASADO: 'Atrasado',
    PROCESSANDO: 'Processando'
  };
  return statusLabels[status] || status;
};

const FaturaPublicaView = ({ fatura, error, onClose, onPagamento }) => {
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!fatura) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const { prestacao, estudante, instituicao, pagamento } = fatura.data;
  const dataVencimento = new Date(prestacao.dataVencimento);
  const hoje = new Date();
  const diasAteVencimento = Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24));

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6">
            Prestação {prestacao.mes}
          </Typography>
          <Chip
            label={getStatusLabel(prestacao.status)}
            color={getStatusColor(prestacao.status)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
      </Box>

      {/* Valor e Vencimento */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1">Valor da Prestação</Typography>
        <Typography variant="h5" gutterBottom>
          {formatarMoeda(prestacao.valor)} MZN
        </Typography>
        
        <Typography variant="body1" gutterBottom>
          Data de Vencimento: {dataVencimento.toLocaleDateString()}
        </Typography>

        <Alert 
          severity={diasAteVencimento > 5 ? "info" : "warning"}
          sx={{ mt: 1 }}
          icon={<InfoIcon />}
        >
          {diasAteVencimento > 0
            ? `Faltam ${diasAteVencimento} dias para o vencimento`
            : `Vencido há ${Math.abs(diasAteVencimento)} dias`}
        </Alert>
      </Box>

      {/* Detalhes */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Facturado à:
            </Typography>
            <Typography>{estudante.nome}</Typography>
            <Typography>{estudante.endereco}</Typography>
            <Typography>NUIT: {estudante.nuit || 'N/A'}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pagar à:
            </Typography>
            <Typography>{instituicao.nome}</Typography>
            <Typography>NUIT: {instituicao.nuit || 'N/A'}</Typography>
            <Typography>Email: info@zenkroll.co.mz</Typography>
            <Typography>Tel: (+258) 84 635 1111 | 86 635 1111</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Tabela de Valores */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Mensalidade {prestacao.mes}</TableCell>
              <TableCell align="right">{formatarMoeda(pagamento.valorBase)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">Sub Total</TableCell>
              <TableCell align="right">{formatarMoeda(pagamento.valorBase)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">IVA (16%)</TableCell>
              <TableCell align="right">{formatarMoeda(pagamento.valorIVA)} MT</TableCell>
            </TableRow>
            {pagamento.valorMulta > 0 && (
              <TableRow>
                <TableCell align="right">
                  <Typography color="error">Multa por Atraso</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography color="error">
                    {formatarMoeda(pagamento.valorMulta)} MT
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell align="right">
                <Typography variant="subtitle1">Total</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1">
                  {formatarMoeda(pagamento.valorTotal)} MT
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Histórico de Transações */}
      <Typography variant="subtitle1" gutterBottom>
        Histórico de Transações
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Referência</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!prestacao.pagamentos || prestacao.pagamentos.length === 0) ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Sem transações</TableCell>
              </TableRow>
            ) : (
              prestacao.pagamentos.map((pagamentoItem, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {new Date(pagamentoItem.dataPagamento).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{pagamentoItem.metodoPagamento}</TableCell>
                  <TableCell>{pagamentoItem.referencia || 'N/A'}</TableCell>
                  <TableCell align="right">
                    {formatarMoeda(pagamentoItem.valorPago)} MT
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} align="right">Balanço</TableCell>
              <TableCell align="right">{formatarMoeda(pagamento.valorTotal)} MT</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {/* Regras de Multa se Atrasado */}
      {prestacao.status === 'ATRASADO' && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Regras de Multa:
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Até 5 dias: 20% do valor</li>
              <li>Até 10 dias: 50% do valor</li>
              <li>Após 10 dias: 100% do valor</li>
            </ul>
          </Typography>
        </Alert>
      )}

      {/* Botões de Ação */}
      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="contained" onClick={onPagamento}>
          Prosseguir com Pagamento
        </Button>
      </Box>
    </Paper>
  );
};

FaturaPublicaView.propTypes = {
  fatura: PropTypes.shape({
    data: PropTypes.shape({
      prestacao: PropTypes.shape({
        id: PropTypes.number.isRequired,
        mes: PropTypes.string.isRequired,
        valor: PropTypes.number.isRequired,
        status: PropTypes.string.isRequired,
        dataVencimento: PropTypes.string.isRequired,
        pagamentos: PropTypes.arrayOf(PropTypes.shape({
          dataPagamento: PropTypes.string.isRequired,
          metodoPagamento: PropTypes.string.isRequired,
          referencia: PropTypes.string,
          valorPago: PropTypes.number.isRequired
        })).isRequired
      }).isRequired,
      estudante: PropTypes.shape({
        nome: PropTypes.string.isRequired,
        endereco: PropTypes.string,
        nuit: PropTypes.string
      }).isRequired,
      instituicao: PropTypes.shape({
        nome: PropTypes.string.isRequired,
        nuit: PropTypes.string
      }).isRequired,
      pagamento: PropTypes.shape({
        valorBase: PropTypes.number.isRequired,
        valorIVA: PropTypes.number.isRequired,
        valorMulta: PropTypes.number,
        valorTotal: PropTypes.number.isRequired
      }).isRequired
    }).isRequired
  }),
  error: PropTypes.string,
  onClose: PropTypes.func,
  onPagamento: PropTypes.func,
};

FaturaPublicaView.defaultProps = {
  error: null,
  onClose: () => {},
  onPagamento: () => {},
};

export default FaturaPublicaView;
